/**
 * The time clock API (labor-service /api/timeclock/v2, services/time_clock: the one clock on
 * time_clock_entries, the rows Payroll pays). `headers` is a function returning the
 * session's headers: the restaurant account on a shared clock (lib/pwaAccount or the back
 * office), the staff session (pos_token) on the staff app.
 *
 * Every refusal comes back as a TimeClockError with the server's own message and code
 * (already clocked in, not clocked in, wrong PIN with tries left, PIN locked, ...).
 */
import { API } from '@/config/api';

const BASE = `${API}/timeclock/v2`;

export class TimeClockError extends Error {
  constructor(message, { status = 0, code = null, detail = null, network = false } = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.network = network;
  }
}

export function errorText(detail, fallback = 'Something went wrong') {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (detail.message) return detail.message;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || String(d)).join('; ');
  return fallback;
}

async function call(headers, method, path, body) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', ...(headers ? headers() : {}) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (e) {
    throw new TimeClockError('No connection to the server', { network: true });
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data.detail;
    throw new TimeClockError(errorText(detail, `Request failed (${res.status})`), {
      status: res.status, code: detail?.code || null, detail,
    });
  }
  return data;
}

const q = (params) => {
  const s = new URLSearchParams(Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== ''));
  const str = s.toString();
  return str ? `?${str}` : '';
};

export const timeClockApi = (headers) => ({
  me: () => call(headers, 'GET', '/me'),
  roster: () => call(headers, 'GET', '/roster'),
  identify: (who) => call(headers, 'POST', '/identify', who),
  state: (staffId) => call(headers, 'GET', `/state${q({ staff_id: staffId })}`),
  punch: (body) => call(headers, 'POST', '/punch', body),
  sync: (deviceId, punches) => call(headers, 'POST', '/punches/sync', { device_id: deviceId, punches }),
  events: (params) => call(headers, 'GET', `/events${q(params)}`),
  // manager
  live: () => call(headers, 'GET', '/live'),
  entries: (params) => call(headers, 'GET', `/entries${q(params)}`),
  createEntry: (body) => call(headers, 'POST', '/entries', body),
  editEntry: (id, body) => call(headers, 'PATCH', `/entries/${id}`, body),
  approveEntry: (id, reason) => call(headers, 'POST', `/entries/${id}/approve`, { reason }),
  clockOutEntry: (id, reason, clockOut) => call(headers, 'POST', `/entries/${id}/clock-out`, { reason, clock_out: clockOut || null }),
  audit: (id) => call(headers, 'GET', `/entries/${id}/audit`),
  review: () => call(headers, 'GET', '/review'),
  resolvePunch: (id, resolution, reason) => call(headers, 'POST', `/review/punches/${id}/resolve`, { resolution, reason }),
  settings: () => call(headers, 'GET', '/settings'),
  saveSettings: (body) => call(headers, 'PUT', '/settings', body),
  timecards: (fromDate, toDate) => call(headers, 'GET', `/payroll/timecards${q({ from_date: fromDate, to_date: toDate })}`),
});

export const ACTION_LABEL = {
  CLOCK_IN: 'Clock in',
  BREAK_START: 'Start break',
  BREAK_END: 'End break',
  CLOCK_OUT: 'Clock out',
};

export const STATE_LABEL = {
  OFF: 'Not clocked in',
  WORKING: 'On the clock',
  ON_BREAK: 'On break',
};
